import { LandMetaData } from 'constants/FarmboxConstants/stage4.constants';
import Notification from 'shared/components/Notification';
import { NotificationTypes } from 'enums/notificationTypes';
import axiosInstance from 'interceptor/axiosInstance';
import { FieldDetails, Stage4MetaData } from 'models/Farmbox/field.model';
import { useState } from 'react';
import { generatePath } from 'react-router-dom';
import { ApiRoutes } from 'routes/routeConstants/apiRoutes';
import { deserialize, serialize } from 'serializr';
import { StageUpdatingMessage } from 'constants/constants';
import {
  filterOutCropsEmptyObjects,
  filterOutEmptyObjects,
} from 'shared/utils/filterOutEmptyObjects';

const FarmFieldService = () => {
  const [loading, setLoading] = useState<boolean>(false);
  const [buttonLoading, setButtonLoading] = useState<boolean>(false);
  const [draftButtonLoading, setDraftButtonLoading] = useState<boolean>(false);
  const [fieldData, setFieldData] = useState<FieldDetails>();
  const [landUseMetaData, setLandUseMetaData] = useState<Stage4MetaData[]>([]);
  const [landManagementMetaData, setLandManagementMetaData] = useState<
    Stage4MetaData[]
  >([]);
  const { STAGE_4 } = StageUpdatingMessage;

  const fetchFieldData = async (pathParams: { commodityId?: string }) => {
    const { commodityId } = pathParams;

    try {
      setLoading(true);
      const url = generatePath(ApiRoutes.CREATE_COMMODITY_FIELD, {
        commodityId,
      });
      const response = await axiosInstance.get(url);

      const fieldResponse = deserialize(FieldDetails, response.data);

      setFieldData(fieldResponse);

      return fieldResponse;
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const updateFieldData = async (
    commodityId?: string,
    fieldData?: FieldDetails
  ) => {
    try {
      setButtonLoading(true);
      const url = generatePath(ApiRoutes.CREATE_COMMODITY_FIELD, {
        commodityId,
      });

      if (fieldData?.commodity?.landUseChanges)
        fieldData.commodity.landUseChanges = filterOutEmptyObjects(
          fieldData.commodity.landUseChanges
        );

      if (fieldData?.commodity?.landManagementChanges)
        fieldData.commodity.landManagementChanges = filterOutEmptyObjects(
          fieldData.commodity.landManagementChanges
        );

      if (fieldData?.commodity?.cropResidues)
        fieldData.commodity.cropResidues = filterOutCropsEmptyObjects(
          fieldData.commodity.cropResidues
        );

      const serializedData = serialize(FieldDetails, fieldData);

      await axiosInstance.put(url, serializedData);
      Notification({
        message: STAGE_4,
        description: '',
        type: NotificationTypes.SUCCESS,
      });
      return true;
    } catch (error) {
      return false;
    } finally {
      setButtonLoading(false);
    }
  };

  const updateFieldDataAsDraft = async (
    commodityId?: string,
    fieldData?: FieldDetails,
    params?: { saveDraft: boolean }
  ) => {
    try {
      setDraftButtonLoading(true);
      const url = generatePath(ApiRoutes.CREATE_COMMODITY_FIELD, {
        commodityId,
      });

      if (fieldData?.commodity?.landUseChanges)
        fieldData.commodity.landUseChanges = filterOutEmptyObjects(
          fieldData.commodity.landUseChanges
        );

      if (fieldData?.commodity?.landManagementChanges)
        fieldData.commodity.landManagementChanges = filterOutEmptyObjects(
          fieldData.commodity.landManagementChanges
        );

      if (fieldData?.commodity?.cropResidues)
        fieldData.commodity.cropResidues = filterOutCropsEmptyObjects(
          fieldData.commodity.cropResidues
        );

      const serializedData = serialize(FieldDetails, fieldData);

      await axiosInstance.put(url, serializedData, { params: { draft: params?.saveDraft } });
      Notification({
        message: STAGE_4,
        description: '',
        type: NotificationTypes.SUCCESS,
      });
      return true;
    } catch (error) {
      return false;
    } finally {
      setDraftButtonLoading(false);
    }
  };

  const fetchLandMetaData = async (metaType?: LandMetaData) => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(ApiRoutes.FETCH_META_DATA, {
        params: { metadata_type: metaType },
      });
      const desiredMetaData = deserialize(
        Stage4MetaData,
        response.data.data.metadata
      ) as unknown as Stage4MetaData[];

      if (metaType === LandMetaData.LAND_USE_META) {
        setLandUseMetaData(desiredMetaData);
      } else {
        setLandManagementMetaData(desiredMetaData);
      }
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  return {
    loading,
    fieldData,
    landUseMetaData,
    landManagementMetaData,
    fetchFieldData,
    fetchLandMetaData,
    updateFieldData,
    buttonLoading,
    updateFieldDataAsDraft,
    draftButtonLoading,
  };
};
export default FarmFieldService;
